import { useApolloClient, useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { EventUpdateInput } from "@/graphql/__generated__/graphql";
import type { EventNode, TicketKindNode } from "@/types/event";

import { EVENT_BY_SLUG_QUERY } from "@/graphql/event-detail";
import {
  REPLACE_TICKET_KIND_MUTATION,
  UPDATE_EVENT_MUTATION,
} from "@/graphql/event-mutations";
import Bugsnag from "@/utils/bugsnag";
import { formatDescription } from "@/utils/formatStrings";

const IMMEDIATE_KEYS: ReadonlySet<string> = new Set(["status", "visibility"]);

const REQUIRED_FIELDS = ["location_name", "starts_at", "title"] as const;

// Fields from EventNode that map directly to EventUpdateInput
const EVENT_UPDATE_KEYS: ReadonlySet<string> = new Set([
  "description",
  "ends_at",
  "flyer_url",
  "location_city",
  "location_country",
  "location_geo",
  "location_name",
  "location_neighborhood",
  "location_place_id",
  "location_postal_code",
  "location_state",
  "location_street",
  "starts_at",
  "status",
  "timezone",
  "title",
  "visibility",
]);

export interface EventEdit {
  dirty: boolean;
  error: null | string;
  patchEvent: (fields: Partial<EventNode>) => void;
  patchTicketKind: (fields: Partial<TicketKindNode>) => void;
  persistNow: () => Promise<void>;
  saving: boolean;
}

export function useEventEdit(slug: string, eventId: null | string): EventEdit {
  const client = useApolloClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [dirty, setDirty] = useState(false);

  const persistingRef = useRef(false);
  const queuedRef = useRef(false);

  // Snapshot of the event node at last save (for dirty detection)
  const savedSnapshotRef = useRef<EventNode | null>(null);

  // Track which required fields have been persisted (were non-empty at load)
  const persistedRequiredRef = useRef<Set<string>>(new Set());

  // Initialize snapshot and persisted required fields from current cache
  useEffect(() => {
    const node = readEventNode(client, slug);
    console.log(
      "[useEventEdit] snapshot init effect — node:",
      !!node,
      "existing snapshot:",
      !!savedSnapshotRef.current,
    );
    if (!node || savedSnapshotRef.current) return;
    console.log(
      "[useEventEdit] setting snapshot, node.location_geo:",
      node.location_geo,
    );
    savedSnapshotRef.current = structuredClone(node);
    const filled = new Set<string>();
    if (node.title?.trim()) filled.add("title");
    if (node.starts_at) filled.add("starts_at");
    if (node.location_name?.trim()) filled.add("location_name");
    persistedRequiredRef.current = filled;
  }, [client, slug]);

  const [updateEvent] = useMutation(UPDATE_EVENT_MUTATION);
  const [replaceTicketKind] = useMutation(REPLACE_TICKET_KIND_MUTATION);

  const persist = useCallback(async () => {
    if (!eventId) return;
    const node = readEventNode(client, slug);
    const snapshot = savedSnapshotRef.current;
    if (!node) return;

    if (persistingRef.current) {
      queuedRef.current = true;
      return;
    }
    persistingRef.current = true;
    setSaving(true);

    try {
      // Diff event fields against saved snapshot (use empty baseline when
      // snapshot hasn't been initialised so every populated field is sent)
      const baseline = snapshot ?? ({} as Partial<EventNode>);
      const eventPatch: EventUpdateInput = {};
      let hasEventChanges = false;
      for (const key of EVENT_UPDATE_KEYS) {
        const current = node[key as keyof EventNode];
        const saved = baseline[key as keyof EventNode];
        if (JSON.stringify(current) !== JSON.stringify(saved)) {
          let value = current as null | string | undefined;
          // Never clear location_geo — it should only be replaced, not nulled
          if (key === "location_geo" && !value) continue;
          // Format description and title before saving
          if (key === "description" && typeof value === "string") {
            value = formatDescription(value);
          }
          if (key === "title" && typeof value === "string") {
            value = formatDescription(value);
          }
          (eventPatch as Record<string, unknown>)[key] = value;
          hasEventChanges = true;
        }
      }

      console.log("[persist] snapshot is null?", snapshot === null);
      console.log("[persist] eventPatch:", JSON.stringify(eventPatch));
      console.log("[persist] hasEventChanges:", hasEventChanges);
      console.log("[persist] node.location_geo:", node.location_geo);
      console.log("[persist] snapshot.location_geo:", snapshot?.location_geo);

      if (hasEventChanges) {
        await updateEvent({
          variables: { id: eventId, set: eventPatch },
        });
      }

      // Diff ticket kind fields
      const currentTier = getTicketKind(node);
      const savedTier = getTicketKind(baseline as EventNode);

      const ticketChanged =
        currentTier &&
        (currentTier.price_cents > 0 || currentTier.name) &&
        (!savedTier ||
          currentTier.currency !== savedTier.currency ||
          currentTier.description !== savedTier.description ||
          currentTier.name !== savedTier.name ||
          currentTier.price_cents !== savedTier.price_cents ||
          currentTier.quantity_total !== savedTier.quantity_total);

      if (ticketChanged) {
        await replaceTicketKind({
          variables: {
            eventId,
            newTicket: {
              currency: currentTier.currency,
              description: currentTier.description,
              event_id: eventId,
              name: currentTier.name || "General Admission",
              price_cents: currentTier.price_cents,
              quantity_total: currentTier.quantity_total ?? 10,
            },
          },
        });
      }

      // Update snapshot to reflect saved state
      const updatedNode = readEventNode(client, slug);
      if (updatedNode) {
        savedSnapshotRef.current = structuredClone(updatedNode);
      }

      // Update persisted required fields
      for (const field of REQUIRED_FIELDS) {
        const val = node[field];
        if (val && String(val).trim()) {
          persistedRequiredRef.current.add(field);
        }
      }

      setDirty(false);
      setError(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "failed to save";
      console.error("[persist] save failed:", e);
      Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
      setError(msg);
    } finally {
      persistingRef.current = false;
      setSaving(false);

      if (queuedRef.current) {
        queuedRef.current = false;
        void persist();
      }
    }
  }, [client, eventId, replaceTicketKind, slug, updateEvent]);

  const persistNow = useCallback(async () => {
    await persist();
  }, [persist]);

  const patchEvent = useCallback(
    (incoming: Partial<EventNode>) => {
      const cached = client.readQuery({
        query: EVENT_BY_SLUG_QUERY,
        variables: { slug },
      });
      if (!cached?.eventCollection?.edges[0]) return;

      const currentNode = cached.eventCollection.edges[0].node;

      // Block publish if required fields are missing
      let fields = incoming;
      if (fields.status === "published") {
        const merged = { ...currentNode, ...fields };
        const missing: string[] = [];
        if (!merged.title?.trim()) missing.push("title");
        if (!merged.starts_at) missing.push("start date");
        if (!merged.ends_at) missing.push("end date");
        if (!merged.location_name?.trim()) missing.push("location");
        if (!merged.location_geo) missing.push("location coordinates");
        if (missing.length > 0) {
          const msg = `cannot publish — missing ${missing.join(", ")}`;
          console.error("[patchEvent]", msg);
          setError(msg);
          const { status: _status, ...rest } = fields;
          if (Object.keys(rest).length === 0) return;
          fields = rest;
        }
      }

      const updatedNode = { ...currentNode, ...fields };

      console.log("[patchEvent] incoming fields:", {
        hasLocationGeo: "location_geo" in fields,
        location_geo: (fields as Record<string, unknown>).location_geo,
      });
      console.log(
        "[patchEvent] updatedNode.location_geo:",
        updatedNode.location_geo,
      );

      client.writeQuery({
        data: {
          ...cached,
          eventCollection: {
            ...cached.eventCollection,
            edges: [
              {
                ...cached.eventCollection.edges[0],
                node: updatedNode,
              },
            ],
          },
        },
        query: EVENT_BY_SLUG_QUERY,
        variables: { slug },
      });

      setDirty(true);

      // Check if we should auto-persist
      const hasImmediate = Object.keys(fields).some((k) =>
        IMMEDIATE_KEYS.has(k),
      );

      const currentStatus = updatedNode.status;
      const isPrePublish = currentStatus !== "published";

      const hasNewRequiredField =
        isPrePublish &&
        REQUIRED_FIELDS.some(
          (key) =>
            key in fields &&
            fields[key as keyof typeof fields] &&
            String(fields[key as keyof typeof fields]).trim() &&
            !persistedRequiredRef.current.has(key),
        );

      if (hasImmediate || hasNewRequiredField) {
        void persist();
      }
    },
    [client, persist, slug],
  );

  const patchTicketKind = useCallback(
    (fields: Partial<TicketKindNode>) => {
      const cached = client.readQuery({
        query: EVENT_BY_SLUG_QUERY,
        variables: { slug },
      });
      if (!cached?.eventCollection?.edges[0]) return;

      const currentNode = cached.eventCollection.edges[0].node;
      const ticketCollection = currentNode.event_ticket_kindCollection;
      const currentTier = ticketCollection?.edges[0]?.node;

      const updatedTier = currentTier
        ? { ...currentTier, ...fields }
        : {
            __typename: "event_ticket_kind" as const,
            currency: "usd",
            description: null,
            id: "",
            name: "General Admission",
            price_cents: 0,
            price_service_fee: 0,
            quantity_sold: 0,
            quantity_total: 10,
            status: "active",
            ...fields,
          };

      const updatedNode = {
        ...currentNode,
        event_ticket_kindCollection: {
          __typename: "event_ticket_kindConnection" as const,
          edges: [
            {
              __typename: "event_ticket_kindEdge" as const,
              node: updatedTier,
            },
          ],
        },
      };

      client.writeQuery({
        data: {
          ...cached,
          eventCollection: {
            ...cached.eventCollection,
            edges: [
              {
                ...cached.eventCollection.edges[0],
                node: updatedNode,
              },
            ],
          },
        },
        query: EVENT_BY_SLUG_QUERY,
        variables: { slug },
      });

      setDirty(true);
    },
    [client, slug],
  );

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      if (dirty) {
        void persist();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { dirty, error, patchEvent, patchTicketKind, persistNow, saving };
}

// ── Helpers ────────────────────────────────────────────────────────

function getTicketKind(node: EventNode): null | TicketKindNode {
  return node.event_ticket_kindCollection?.edges[0]?.node ?? null;
}

function readEventNode(
  client: ReturnType<typeof useApolloClient>,
  slug: string,
): EventNode | null {
  const cached = client.readQuery({
    query: EVENT_BY_SLUG_QUERY,
    variables: { slug },
  });
  return cached?.eventCollection?.edges[0]?.node ?? null;
}
